import React, { useContext, useEffect, useMemo } from 'react';

import { RawDraftContentBlock, RawDraftEntityRange, RawDraftInlineStyleRange } from 'draft-js';
import { isEmpty, isNil } from 'ramda';

import i18n from 'i18n';
import { isDefined } from 'utils/ramda';

import { LANDING_PAGE_ROLES } from '../../../../constants';
import { IPage } from '../../../../utils/itineraryDetails/types';
import {
  EBlockType,
  IAccordionBlockData,
  IAccordionSpoiler,
  IBlock,
  IFastFactsBlockData,
  TBlock,
} from '../../../__blocks';
import { EPageRole } from '../../../LandingV2/Page/types';
import { ILandingSettings } from '../../../LandingV2/types';
import { PDFDataContext } from '../../context';
import Page from '../Page';
import { BLOCK_ROLE } from '../Page/Blocks/Text';
import { defaultPagesFilterFn } from './constants';
import { ITextCustomBlock, PDFRawDraftContentBlock, PreparedPageBlock, TPagesFilterFn } from './types';

function OtherPages({ pagesFilterFn = defaultPagesFilterFn, onReady = () => {} }) {
  const { page } = useContext(PDFDataContext);
  const { settings } = page;
  const blocks = useMemo(() => prepareBlocks(page.landing.pages, pagesFilterFn, settings), [page, pagesFilterFn]);

  // const blocks = useMemo(
  //   () => pipe(getInOr([], 'landing.pages'), reject(getIn('hidden')), filter(pagesFilterFn), testPrepareBlocks)(page),
  //   [page, pagesFilterFn],
  // );

  useEffect(() => {
    if (isEmpty(blocks)) onReady();
  }, [blocks, onReady]);

  if (isEmpty(blocks)) return null;

  //@ts-ignore
  return <Page blocks={blocks} onReady={onReady} />;
}

export default OtherPages;

function prepareBlocks(pages: IPage[], filterFn: TPagesFilterFn, settings: ILandingSettings = {}) {
  const blocks: PreparedPageBlock[] = [];
  const getUniqueImage = makeUniqueImageList();
  pages
    .filter((p) => filterFn(p) && !p.hidden)
    .forEach((data) => {
      const image = getUniqueImage(data);
      const pageTitle = getPageTitle(data, settings);

      const pageBlocks = getPreparedPageBlocks(flattenBlocks(data?.blocks)).map((b) => ({
        ...b,
        pageTitle,
        pageType: data.type,
        primaryImageCoords: data.primaryImageCoords,
        image,
      }));

      if (blocks[blocks.length - 1]?.type === 'header' && pageBlocks[0]?.type === 'header') {
        blocks.splice(blocks.length - 1, 1);
      }

      blocks.push(...pageBlocks);
    });

  return blocks;
}

export function getPreparedPageBlocks(blocks: TBlock[] = []) {
  return (
    blocks
      .filter((b) => ['header', 'text', 'list', 'accordion', 'document', 'checkpoints', 'trip_event'].includes(b.type))
      // Reduce generic
      .reduce<PreparedPageBlock[]>((acc, cur) => {
        if (cur.type === 'list') {
          const paragraphs = getParagraphsFromList(cur.data);
          acc.push(...paragraphs);
        } else if (cur.type === 'text') {
          if (cur.data.template === 'box' || cur.data.template === 'notification') {
            //@ts-ignore
            acc.push({ ...cur, type: 'textbox' });
          } else {
            const fragments = getFragmentsFromTextBlock(cur, true);
            acc.push(...fragments);
            if (cur.data.text === i18n.t('proposal_content.event_details.no_events')) {
              acc.push(cur);
            }
          }
        } else if (cur.type === 'accordion') {
          const paragraphs = getParagraphsFromAccordion(cur.data);
          acc.push(...paragraphs);
        } else {
          acc.push(cur);
        }

        return acc;
      }, [])
  );
}

export function getFragmentsFromTextBlock(block: IBlock<EBlockType.Text> | ITextCustomBlock, splitEveryBlock: boolean) {
  const paragraphs = paragraphise(block?.data?.raw?.blocks, splitEveryBlock);
  let order = 0;
  let wasListItem = false;

  function blockFactory() {
    return {
      ...block,
      data: {
        raw: {
          blocks: [],
          entityMap: block?.data?.raw?.entityMap,
        },
        text: block?.data?.text,
      },
    };
  }

  return paragraphs.reduce((acc, cur, i) => {
    const newBlock = blockFactory();

    if (cur[0]?.type === 'unordered-list-item') {
      const styleOffset = 2;
      const inlineStyles = cur[0].inlineStyleRanges || [];
      const entities = cur[0].entityRanges || [];
      cur[0].text = `• ${cur[0].text}`;
      cur[0].type = 'unstyled';
      cur[0].inlineStyleRanges = inlineStyles.map((style) => ({ ...style, offset: style.offset + styleOffset }));
      cur[0].entityRanges = entities.map((elem) => ({ ...elem, offset: elem.offset + styleOffset }));
    }

    if (cur[0]?.type === 'ordered-list-item') {
      order += 1;
      const styleOffset = String(order)?.length + 2;
      const inlineStyles = cur[0].inlineStyleRanges || [];
      const entities = cur[0].entityRanges || [];
      cur[0].text = `${order}. ${cur[0].text}`;
      cur[0].type = 'unstyled';
      cur[0].inlineStyleRanges = inlineStyles.map((style) => ({ ...style, offset: style.offset + styleOffset }));
      cur[0].entityRanges = entities.map((elem) => ({ ...elem, offset: elem.offset + styleOffset }));
      wasListItem = true;
    } else if (wasListItem) {
      order = 0;
    }

    if (cur.length === 1 && isSNLBlock(cur[0])) {
      newBlock.data.text = ' ';
      //@ts-ignore
      delete newBlock.data.raw;
    } else {
      //@ts-ignore
      newBlock.data.raw.blocks = cur;
    }

    if (i < paragraphs.length - 1) {
      //@ts-ignore
      newBlock.role = BLOCK_ROLE.LINE;
    }
    //@ts-ignore
    acc.push(newBlock);

    return acc;
  }, []);
}

export function paragraphise(blocks: Array<RawDraftContentBlock> = [], splitEveryBlock: boolean) {
  let buffer: Array<PDFRawDraftContentBlock> = [];

  return blocks.reduce<Array<PDFRawDraftContentBlock[]>>((acc, cur, i) => {
    if (splitEveryBlock) {
      if (cur.text.includes('\n')) {
        cur.text.split('\n').reduce((globalOffset, t, index) => {
          const inlineStyleRanges = getRangesWithUpdatedOffset(cur.inlineStyleRanges, globalOffset, index);
          const entityRanges = getRangesWithUpdatedOffset(cur.entityRanges, globalOffset, index);
          acc.push([
            {
              ...cur,
              inlineStyleRanges,
              entityRanges,
              text: t === '' ? ' ' : t,
              isTextLine: true,
            },
          ]);
          return globalOffset + t.length;
        }, 0);
      } else {
        acc.push([{ ...cur, isTextLine: true }]);
      }
      buffer = [];
    } else if (isSNLBlock(cur)) {
      if (buffer.length > 0) {
        acc.push(buffer);
        buffer = [];
      }
    } else {
      buffer.push(cur);
    }

    if (i === blocks.length - 1 && buffer.length > 0) {
      acc.push(buffer);
    }

    return acc;
  }, []);
}

// is Soft New Line block
function isSNLBlock(b: RawDraftContentBlock) {
  const typesCollection = [
    'unstyled',
    'left',
    'right',
    'center',
    'justify',
    'unordered-list-item',
    'ordered-list-item',
    'blockquote',
  ];
  return (
    !b.text &&
    typesCollection.includes(b.type) &&
    isEmpty(b.data) &&
    isEmpty(b.entityRanges) &&
    isEmpty(b.inlineStyleRanges)
  );
}

/**
 * @deprecated Used for old spoilers with body
 */
function handleParagraphsOld(acc: PreparedPageBlock[], cur: IAccordionSpoiler) {
  let spoilerParagraphs: Array<PreparedPageBlock> = [];

  if (!isEmpty(cur.body?.raw) && !isNil(cur.body?.raw)) {
    spoilerParagraphs = getFragmentsFromTextBlock(
      {
        type: EBlockType.Text,
        role: BLOCK_ROLE.PARAGRAPH,
        data: {
          raw: cur?.body?.raw,
          text: cur?.body?.text,
        },
      },
      true,
    );

    if (spoilerParagraphs.length > 0) {
      // @ts-ignore
      spoilerParagraphs[0].data.title = cur.title;
    }
  } else if (cur.body?.placeholder) {
    spoilerParagraphs = [
      {
        type: 'text',
        role: BLOCK_ROLE.LINE,
        data: {
          placeholder: cur.body?.placeholder,
          title: cur.title,
        },
      },
    ];
  } else if (cur.body?.text) {
    spoilerParagraphs = [
      {
        type: 'text',
        role: BLOCK_ROLE.LINE,
        data: {
          text: cur.body?.text,
          title: cur.title,
        },
      },
    ];
  }

  acc.push(...spoilerParagraphs);

  return acc;
}

function handleParagraphs(acc: PreparedPageBlock[], cur: IAccordionSpoiler) {
  let spoilerParagraphs: Array<PreparedPageBlock> = [];

  if (cur?.title) {
    spoilerParagraphs = [
      {
        type: 'text',
        role: BLOCK_ROLE.LINE,
        data: {
          title: cur.title,
        },
      },
    ];
  }

  if (isDefined(cur?.children)) {
    const handledChildren = getPreparedPageBlocks(cur.children);
    spoilerParagraphs = [...spoilerParagraphs, ...handledChildren];
  }

  acc.push(...spoilerParagraphs);

  return acc;
}

function getParagraphsFromAccordion({ spoilers }: IAccordionBlockData) {
  return spoilers.reduce<PreparedPageBlock[]>((acc, cur) => {
    if (isDefined(cur?.children)) {
      return handleParagraphs(acc, cur);
    }

    return handleParagraphsOld(acc, cur);
  }, []);
}

function getParagraphsFromList(data: IFastFactsBlockData) {
  return data.items.reduce<PreparedPageBlock[]>((acc, item) => {
    if (item.title) {
      acc.push({
        type: 'text',
        role: BLOCK_ROLE.LINE,
        data: {
          title: item.title,
        },
      });
    }

    const itemParagraphs = getFragmentsFromTextBlock(
      {
        type: 'text',
        role: BLOCK_ROLE.PARAGRAPH,
        data: {
          raw: item.desc?.raw,
          text: item.desc?.text,
        },
      },
      true,
    );
    acc.push(...itemParagraphs);

    return acc;
  }, []);
}

const flattenBlocks = (blocks: any[]): any => {
  return blocks.reduce((acc, block) => {
    return [...acc, block, ...(block?.children?.length > 0 ? flattenBlocks(block.children) : [])];
  }, []);
};

const makeUniqueImageList = () => {
  const uniqueImages: Array<IPage['primaryImageUrl' | 'primaryImageVersion800']> = [];
  return ({ primaryImageVersion800, primaryImageUrl, role }: IPage) => {
    let image = primaryImageVersion800 || primaryImageUrl;
    if (role !== LANDING_PAGE_ROLES.DAY) {
      return image;
    }
    if (uniqueImages.includes(image)) {
      image = null;
    } else {
      uniqueImages.push(image);
    }
    return image;
  };
};

export const getRangesWithUpdatedOffset = <T extends RawDraftInlineStyleRange | RawDraftEntityRange>(
  ranges: T[],
  globalOffset: number,
  newLineCharCount: number,
) => {
  return ranges.reduce<T[]>((acc, range) => {
    // Difference between the block offset, global offset, and the number of newline characters (\n).
    const resultOffset = range.offset - globalOffset - newLineCharCount;
    return resultOffset >= 0 ? [...acc, { ...range, offset: resultOffset }] : acc;
  }, []);
};

export function getPageTitle({ title, subTitle, headline, subheadline, role }: IPage, settings: ILandingSettings) {
  const name = settings?.textOverImages ? title : headline;
  const subName = settings?.textOverImages ? subTitle : subheadline;

  return subName && role === EPageRole.DAY ? `${name}\n${subName}` : name;
}
